import React from "react";
import styled from "styled-components";
import { Col, Row, Tab, Tabs } from "react-bootstrap";
import {
  FilterInfo,
  MainUnits,
  UnitWithLessons,
  Lesson,
  LessonOrUnitGroup,
  PracticeMetadataResult,
  CoursesResult,
} from "../../common/api";
import { ApiContext } from "../../common/context/ApiContext";
import { useLoadingIndicator } from "../../common/context/LoadingIndicatorContext";
import { Account } from "../../common/context/AccountContext";
import { UnitItemBody } from "../UnitItemBody";
import { Link } from "react-router-dom";
import {
  BackNav,
  CallToActionButton,
  Section,
  SectionHeading,
  UnitTilesRow,
} from "../../common/CommonStyles";
import { UnitFilterControls, useUnitFilters } from "../UnitFilters";
import { lessonCornerDecoration, LessonItemBody } from "../LessonItemBody";
import { Checkbox } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSquareCheck,
  faSquareMinus,
} from "@fortawesome/pro-regular-svg-icons";
import { UnitDetailsLayout } from "../UnitDetails";
import { default as UUID } from "uuid";
import { useTranslation } from "react-i18next";
import { packLessonIdAndCustomFlashpackId } from "../../common/utils";
import {
  getSectionsFromUnit,
  SectionWithLessons,
} from "../../common/SectionWithLessons";
import { COLOR_GRAY_EE, COLOR_GRAY_MUTED } from "../../common/colors";

const Body = styled.div`
  margin-top: 15px;
`;

const LessonRow = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  margin-bottom: 0;
  cursor: pointer;
  user-select: none;
`;

const Subhead = styled.span`
  color: ${COLOR_GRAY_MUTED};
`;

type DataState = Readonly<{
  courses: MainUnits | null;
  practiceGroups: [LessonOrUnitGroup] | null;
  filterInfo?: FilterInfo;
}>;

export const LessonsSelector: React.FC<{
  account: Account;
  selectedLessons: ReadonlyMap<string, Lesson>; // "[lesson_id]_[custom_flashpack_id?]"
  setSelectedLessons: (selectedLessons: ReadonlyMap<string, Lesson>) => void; // "[lesson_id]_[custom_flashpack_id?]"
  initialUnitId?: number;
}> = ({ account, selectedLessons, setSelectedLessons, initialUnitId }) => {
  const { t } = useTranslation();
  const [state, setState] = React.useState<DataState>({
    courses: null,
    practiceGroups: null,
  });
  const filters = useUnitFilters(null, state.filterInfo);
  const [selectedUnitId, setSelectedUnitId] = React.useState<number | null>(
    initialUnitId ?? null
  );
  const [currentTab, setCurrentTab] = React.useState("courses");
  const [unitsResult, setUnitsResult] = React.useState<{
    courses: CoursesResult;
    practiceMetadata: PracticeMetadataResult;
  }>();
  const { startLoading: startLoadingUnits, stopLoading: stopLoadingUnits } =
    useLoadingIndicator(true);
  const {
    startLoading: startLoadingUnitWithLessons,
    stopLoading: stopLoadingUnitWithLessons,
  } = useLoadingIndicator(false);
  const { getCourses, getUnitWithLessons, getPracticeMetadata } =
    React.useContext(ApiContext);
  const unitsFetchedForUserVersion = React.useRef<number | null>(null);
  const unitsFetchJobForUserVersion = React.useRef<number | null>(null);
  const [unitWithLessons, setUnitWithLessons] =
    React.useState<UnitWithLessons | null>(null);
  const unitWithLessonsFetchedForUserVersion = React.useRef<number | null>(
    null
  );
  const unitWithLessonsFetchJobForUserVersion = React.useRef<number | null>(
    null
  );
  const unitWithLessonsFetchJobUuid = React.useRef<string | null>(null);
  const [sections, setSections] = React.useState<SectionWithLessons[]>([]);

  React.useEffect(() => {
    (async () => {
      if (
        account.userCurrentVersion !== unitsFetchedForUserVersion.current &&
        account.userCurrentVersion !== unitsFetchJobForUserVersion.current
      ) {
        startLoadingUnits();
        const expectedVersion = (unitsFetchJobForUserVersion.current =
          account.userCurrentVersion);
        unitsFetchJobForUserVersion.current = account.userCurrentVersion;
        const courses = await getCourses(account.user.flags);
        if (expectedVersion !== unitsFetchJobForUserVersion.current) {
          return;
        }
        const practiceMetadata = await getPracticeMetadata(
          account.profile?.id || null,
          {}
        );
        if (expectedVersion !== unitsFetchJobForUserVersion.current) {
          return;
        }
        setUnitsResult({ courses, practiceMetadata });
        unitsFetchedForUserVersion.current =
          unitsFetchJobForUserVersion.current;
        stopLoadingUnits();
      }
    })();
  }, [
    account,
    getCourses,
    getPracticeMetadata,
    startLoadingUnits,
    stopLoadingUnits,
  ]);

  const unitWithLessonsId = unitWithLessons?.id;

  React.useEffect(() => {
    (async () => {
      if (
        selectedUnitId &&
        ((account.userCurrentVersion !==
          unitWithLessonsFetchedForUserVersion.current &&
          account.userCurrentVersion !==
            unitWithLessonsFetchJobForUserVersion.current) ||
          unitWithLessonsId !== selectedUnitId)
      ) {
        startLoadingUnitWithLessons();
        const fetchJobUuid = (unitWithLessonsFetchJobUuid.current = UUID.v4());
        const unitWithLessonsResponse = await getUnitWithLessons(
          selectedUnitId,
          null,
          {}
        );
        if (unitWithLessonsFetchJobUuid.current !== fetchJobUuid) {
          return;
        }
        // We only show sections that have lessons (no special items like "Create custom flashpack" etc).
        setSections(
          getSectionsFromUnit(
            t,
            account.user,
            unitWithLessonsResponse.unit
          ).filter((s) => s.lessons.length > 0)
        );
        setUnitWithLessons(unitWithLessonsResponse.unit);
        unitWithLessonsFetchedForUserVersion.current =
          unitWithLessonsFetchJobForUserVersion.current;
        stopLoadingUnitWithLessons();
      }
    })();
  }, [
    t,
    selectedUnitId,
    unitWithLessonsId,
    account,
    getUnitWithLessons,
    startLoadingUnitWithLessons,
    stopLoadingUnitWithLessons,
  ]);

  React.useEffect(() => {
    if (!unitsResult) {
      return;
    }
    const filterInfo: FilterInfo = {
      all_value: unitsResult.courses.filter_info.all_value,
      filters: unitsResult.courses.filter_info.filters.map((filter) => ({
        key: filter.key,
        name: filter.name,
        values: [...filter.values],
      })),
      section_title_overrides:
        unitsResult.courses.filter_info.section_title_overrides,
    };
    unitsResult.practiceMetadata.filter_info.filters.forEach(
      (practiceFilter) => {
        let found = false;
        filterInfo.filters.forEach((courseFilter) => {
          if (courseFilter.key === practiceFilter.key) {
            found = true;

            practiceFilter.values.forEach((practiceValue) => {
              if (!courseFilter.values.includes(practiceValue)) {
                courseFilter.values.push(practiceValue);
              }
            });
          }
        });
        if (!found) {
          filterInfo.filters.push({
            key: practiceFilter.key,
            name: practiceFilter.name,
            values: [...practiceFilter.values],
          });
        }
      }
    );
    // We don't merge section title overrides (or even store them separately for practice), because
    // we don't show title in lesson assignments.
    setState({
      courses: unitsResult.courses.units,
      practiceGroups: unitsResult.practiceMetadata.groups,
      filterInfo: filterInfo,
    });
  }, [unitsResult]);

  if (!state.courses || !state.practiceGroups) {
    return <></>;
  }

  const filteredCourses = state.courses.units.filter(filters.matches);
  const lessonKey = (lesson: Lesson) =>
    packLessonIdAndCustomFlashpackId(lesson.id, lesson.custom_flashpack_id);
  const setLessonsSelected = (lessons: Lesson[], isSelected: boolean) => {
    const newSelectedLessons = new Map(selectedLessons);
    lessons.forEach((lesson) => {
      if (isSelected) {
        newSelectedLessons.set(lessonKey(lesson), lesson);
      } else {
        newSelectedLessons.delete(lessonKey(lesson));
      }
    });
    setSelectedLessons(newSelectedLessons);
  };
  const areAllUnitLessonsSelected =
    !!unitWithLessons &&
    unitWithLessons.lessons.every((lesson) =>
      selectedLessons.has(lessonKey(lesson))
    );

  const filteredPractice = state.practiceGroups.map((group) => ({
    ...group,
    units: group.units ? group.units.filter(filters.matches) : null,
  }));

  return (
    <Body>
      <Row className="justify-content-center mb-3">
        <Col xs="auto">
          {selectedLessons.size === 0 ? (
            <Subhead>{t("assignments_lesson_selector") + ":"}</Subhead>
          ) : (
            <Subhead>
              <b>{selectedLessons.size}</b>{" "}
              {t("assignments_lessons_selected", {
                count: selectedLessons.size,
              })}
            </Subhead>
          )}
        </Col>
      </Row>
      {selectedUnitId === null ? (
        <>
          <UnitFilterControls filters={filters} />
          <Tabs
            style={{ borderBottom: `1px solid ${COLOR_GRAY_EE}` }}
            variant="underline"
            className="justify-content-center mb-4"
            activeKey={currentTab}
            onSelect={(key) => setCurrentTab(key as string)}
          >
            <Tab
              eventKey="courses"
              title={t("assignments_lessons_courses_tab")}
              tabClassName="px-3"
            >
              <UnitTilesRow>
                {filteredCourses.map((course) => (
                  <Link
                    key={course.id}
                    to=""
                    onClick={(event) => {
                      if (course.id !== unitWithLessonsId) {
                        setUnitWithLessons(null);
                      }
                      setSelectedUnitId(course.id);
                      event.preventDefault();
                    }}
                    style={{ textDecoration: "none" }}
                  >
                    <UnitItemBody unit={course} />
                  </Link>
                ))}
              </UnitTilesRow>
            </Tab>
            <Tab
              eventKey="practice"
              title={t("assignments_lessons_practice_tab")}
              tabClassName="px-3"
            >
              {filteredPractice.map((group) => (
                <Section key={group.group}>
                  <SectionHeading>{group.title}</SectionHeading>
                  <UnitTilesRow>
                    {group.units?.map((unit) => (
                      <Link
                        key={unit.id}
                        to=""
                        onClick={(event) => {
                          if (unit.id !== unitWithLessonsId) {
                            setUnitWithLessons(null);
                          }
                          setSelectedUnitId(unit.id);
                          event.preventDefault();
                        }}
                        style={{ textDecoration: "none" }}
                      >
                        <UnitItemBody unit={unit} />
                      </Link>
                    ))}
                  </UnitTilesRow>
                </Section>
              ))}
            </Tab>
          </Tabs>
        </>
      ) : unitWithLessons === null ? (
        <></>
      ) : (
        <>
          <BackNav
            label={t(
              currentTab === "courses"
                ? "assignments_all_courses"
                : "assignments_all_practice"
            )}
            callback={() => {
              setSelectedUnitId(null);
            }}
          />
          <UnitDetailsLayout
            unit={unitWithLessons}
            sections={sections}
            headerAction={
              <CallToActionButton
                onClick={() =>
                  setLessonsSelected(
                    unitWithLessons.lessons,
                    !areAllUnitLessonsSelected
                  )
                }
              >
                <FontAwesomeIcon
                  icon={
                    areAllUnitLessonsSelected ? faSquareMinus : faSquareCheck
                  }
                />
                {t(
                  areAllUnitLessonsSelected
                    ? "assignments_deselect_all_lessons"
                    : "assignments_all_lessons"
                )}
              </CallToActionButton>
            }
            renderLesson={(lesson) => (
              <LessonRow>
                <Checkbox
                  checked={selectedLessons.has(lessonKey(lesson))}
                  onChange={(event) =>
                    setLessonsSelected([lesson], event.target.checked)
                  }
                />
                <LessonItemBody
                  lesson={lesson}
                  account={account}
                  className="w-100"
                  leftCornerDecoration={lessonCornerDecoration(lesson, account)}
                />
              </LessonRow>
            )}
          />
        </>
      )}
    </Body>
  );
};
